import compact from 'lodash/compact';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { getPreviewHTMLTemplateUrl } from 'modules/campaigns/lib/utils/template';
import { InAppContentDefinition, MESSAGE_FORMAT, MessageMedium } from 'modules/graphql/types';
import { ActionMenu } from 'ui/actionMenu';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { ChevronDown24, OpenNewWindow16, View24 } from 'ui/icons';
import { LinkButton } from 'ui/linkButton';
import { borderRadius, colors, spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';

import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useTemplateQueryContext } from '../../context/TemplateQueryContext';
import { PREVIEW_IN_DEVICE_URL, SETTINGS_DISPLAY_CONTENT } from '../constants';
import { displayContent } from '../content';

// expected as button & action menu together
const ComboButton = styled.div<{ hasDesignError?: boolean }>`
  display: flex;
  > button:first-child {
    border-right: none;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
    ${({ hasDesignError }) => hasDesignError && `border: 2px solid ${colors.red100}`}
  }
  > *:last-child > button {
    border-left: none;
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }
`;

const StyledActionMenu = styled(ActionMenu)`
  a {
    display: inline-flex;
    gap: ${spacing.half};
  }
`;

export const StyledOverflowButton = styled(IconButton)`
  margin-right: -${spacing.eighth};
  border-radius: 0 ${borderRadius.md} ${borderRadius.md} 0;
`;

const StyledLinkButton = styled(LinkButton)`
  display: inline-flex;
  gap: ${spacing.half};
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right-width: 0;
`;

type TooltipToggleProps = {
  showTooltip: boolean;
  children: React.ReactNode;
};

const TooltipToggle = ({ showTooltip, children }: TooltipToggleProps) => {
  return (
    <Tooltip isEnabled={showTooltip} tooltipContent={SETTINGS_DISPLAY_CONTENT.rawDataTooltip}>
      {children}
    </Tooltip>
  );
};

export const EditButtonCombo = ({ size = BUTTON_SIZES.LARGE }: { size?: BUTTON_SIZES }) => {
  const { locale, templateId } = useQueryParamContext();
  const { liveTemplate } = useEditableFieldsContext();
  const { mediumData = {} } = liveTemplate || {};
  const { messageFormat } = mediumData as InAppContentDefinition;
  const isJSON = messageFormat === MESSAGE_FORMAT.Json;
  const { medium } = useTemplateQueryContext();

  const { pathname, search } = useLocation();
  const searchParams = new URLSearchParams(search);
  searchParams.set('view', 'preview');
  const previewInNewTabUrl = `${pathname}?${searchParams.toString()}`;
  const previewOnDeviceUrl = `${PREVIEW_IN_DEVICE_URL}?templateId=${templateId}`;
  const previewHTMLUrl = getPreviewHTMLTemplateUrl(templateId, locale);

  const setViewOptions: ActionMenuOption[] = compact([
    medium === MessageMedium.Email && {
      as: AllowedActionMenuElements.Link,
      content: (
        <>
          {displayContent.btnPreviewInNewTab}
          <OpenNewWindow16 />
        </>
      ),
      id: 'btn-edit-design-preview-in-new-tab',
      href: previewHTMLUrl,
      target: '_blank',
      isExternalLink: true,
    },
    medium === MessageMedium.Email && {
      as: AllowedActionMenuElements.Link,
      content: (
        <>
          {displayContent.btnPreviewOnDevices}
          <OpenNewWindow16 />
        </>
      ),
      id: 'btn-edit-design-preview-on-devices',
      href: previewOnDeviceUrl,
      target: '_blank',
      isExternalLink: true,
    },
  ]);

  /* 10.24.22 update: [TNT-5600] See comments on the ticket
   * <ComboButton> had Edit Design option before and it required hasDesignError props to show
   * if there's design error or not. But recent change removed that option and now it doesn't need to
   * show design error on Preview combo button. But keeping this, in case if we need it again.
   * import { useErrorValidationContext } from '../../context/ErrorValidationContext';
   * const { designContentWarnings, designContentErrors } = useErrorValidationContext();
   * const hasDesignError = !!designContentErrors.length || !!designContentWarnings.length;
   */

  return (
    <TooltipToggle showTooltip={isJSON}>
      <ComboButton>
        <StyledLinkButton
          data-test={'btn-edit-design-action-menu'}
          isDisabled={isJSON}
          size={size}
          variant={BUTTON_VARIANTS.SECONDARY}
          to={previewInNewTabUrl}
        >
          <View24 />
          <span>{displayContent.btnPreview}</span>
        </StyledLinkButton>
        <StyledActionMenu align={MENU_ALIGNMENTS.RIGHT} options={setViewOptions}>
          <StyledOverflowButton
            data-test={'btn-edit-design-action-menu'}
            isDisabled={isJSON}
            size={size}
            variant={BUTTON_VARIANTS.SECONDARY}
          >
            <ChevronDown24 />
          </StyledOverflowButton>
        </StyledActionMenu>
      </ComboButton>
    </TooltipToggle>
  );
};

export default EditButtonCombo;
